import api, { ApiResponse } from '../core/api';

export type WithdrawalStatus = 'pending' | 'approved' | 'processing' | 'paid' | 'rejected' | 'failed' | 'cancelled';
export type KycStatus = 'not_submitted' | 'pending' | 'verified' | 'rejected';
export type AccountStatus = 'pending' | 'verified' | 'rejected' | 'disabled';

export interface PayoutPerson {
  _id: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  userType?: string;
}

export interface TimelineEntry {
  status: string;
  at: string;
  actorType: 'partner' | 'admin' | 'system' | 'gateway';
  note?: string;
}

export interface Withdrawal {
  _id: string;
  requestNumber: string;
  userId: string;
  userType: string;
  user?: PayoutPerson;
  amount: number;
  fee: number;
  netAmount: number;
  currency: string;
  accountSnapshot?: {
    type: 'bank' | 'stripe_connect';
    accountHolderName?: string;
    bankName?: string;
    routingNumber?: string;
    accountNumberMasked?: string;
    stripeAccountId?: string;
    stripePayoutMethodId?: string;
  };
  status: WithdrawalStatus;
  mode: 'manual' | 'automatic';
  gateway: 'manual' | 'stripe';
  source?: 'partner' | 'scheduled';
  autoApproved?: boolean;
  gatewayTransferId?: string;
  gatewayPayoutId?: string;
  gatewayStatus?: string;
  transactionRef?: string;
  paymentMethod?: string;
  failureReason?: string;
  rejectionReason?: string;
  adminNote?: string;
  partnerNote?: string;
  approvedAt?: string;
  paidAt?: string;
  refundedAt?: string;
  attempts?: number;
  timeline: TimelineEntry[];
  createdAt: string;
}

export interface PayoutLogEntry {
  _id: string;
  action: string;
  entity: string;
  actorType: string;
  actorId?: PayoutPerson;
  partnerId?: PayoutPerson;
  fromStatus?: string;
  toStatus?: string;
  amount?: number;
  message?: string;
  metadata?: Record<string, unknown>;
  ip?: string;
  createdAt: string;
}

export interface AutoCheckItem {
  key: string;
  label: string;
  status: 'pass' | 'warn' | 'fail' | 'skip';
  detail: string;
  critical?: boolean;
}

export interface AutoCheck {
  state: 'running' | 'done' | 'error';
  score?: number;
  recommendation?: 'approve' | 'review' | 'reject';
  summary?: string;
  checks?: AutoCheckItem[];
  extracted?: Record<string, string>;
  error?: string;
  ranAt?: string;
  durationMs?: number;
}

export interface PayoutAccountRow {
  _id: string;
  autoCheck?: AutoCheck;
  user?: PayoutPerson;
  userType: string;
  type: 'bank' | 'stripe_connect';
  accountHolderName: string;
  bankName?: string;
  routingNumber?: string;
  bankAccountType?: string;
  country?: string;
  accountNumberMasked?: string;
  isPrimary: boolean;
  status: AccountStatus;
  rejectionReason?: string;
  verifiedAt?: string;
  usableAfter?: string;
  isDuplicate?: boolean;
  stripe?: {
    accountId?: string;
    managed?: boolean;
    detailsSubmitted: boolean;
    payoutsEnabled: boolean;
    requirementsDue: string[];
    disabledReason?: string;
    setupError?: string;
    lastSyncedAt?: string;
  };
  createdAt: string;
}

export interface KycRow {
  _id: string;
  autoCheck?: AutoCheck;
  user?: PayoutPerson;
  userType: string;
  fullName: string;
  dateOfBirth?: string;
  idType: string;
  idNumberMasked: string;
  idNumber?: string;
  taxId?: string;
  taxIdMasked?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  documents: { index: number; kind: string; mimeType: string; size: number; uploadedAt: string }[];
  status: KycStatus;
  rejectionReason?: string;
  submittedAt?: string;
  reviewedAt?: string;
  history: { status: string; at: string; note?: string }[];
}

export interface PayoutSettings {
  withdrawalsEnabled: boolean;
  mode: 'manual' | 'automatic';
  gateway: 'manual' | 'stripe';
  autoApproveMaxAmount: number;
  minAmount: number;
  maxAmount: number;
  dailyMaxAmount: number;
  dailyMaxCount: number;
  maxOpenRequests: number;
  feeType: 'none' | 'flat' | 'percent';
  feeValue: number;
  requireKyc: boolean;
  requireVerifiedAccount: boolean;
  newAccountCoolingHours: number;
  topupHoldDays: number;
  allowedMethods: Array<'bank' | 'stripe_connect'>;
  allowedUserTypes: string[];
  maxAccountsPerPartner: number;
  blockDuplicateAccounts: boolean;
  autoVerifyStripeAccounts: boolean;
  stripe: {
    webhookSecretSet: boolean;
    platformWebhookSecretSet?: boolean;
    webhookSecretLast4?: string;
    platformWebhookSecretLast4?: string;
    accountCountry?: string;
    statementDescriptor?: string;
    managedBankPayouts?: boolean;
    financialAccountId?: string;
  };
  autoPayout: AutoPayoutSchedule;
  autoPayoutStatus?: {
    timezone: string;
    nextRunAt: string | null;
    lastRun: AutoPayoutRunSummary | null;
  };
  updatedAt?: string;
}

export interface AutoPayoutSchedule {
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  /** 0 = Sunday … 6 = Saturday */
  weekDays: number[];
  monthDay: number;
  /** HH:mm, tenant timezone */
  time: string;
  minWalletBalance: number;
}

export interface AutoPayoutRunSummary {
  runKey: string;
  at: string;
  trigger: 'schedule' | 'admin';
  candidates: number;
  created: number;
  paid: number;
  awaitingAdmin: number;
  skipped: number;
  errors: number;
  totalAmount: number;
  skippedPartners?: Array<{ userId: string; name: string; reason: string }>;
}

export interface StripeStatus {
  configured: boolean;
  message?: string;
  sandbox?: boolean;
  stub?: boolean;
  country?: string;
  currency?: string;
  webhookSecretSet: boolean;
  platformWebhookSecretSet?: boolean;
  platform?: { id?: string; name?: string; country?: string; connectEnabled: boolean | null; connectMessage?: string } | null;
  webhookUrl: string;
  balance?: { currency: string; available: number; pending: number }[] | null;
  /** Present when "Pay bank accounts through Stripe" is on. */
  globalPayouts?: { financialAccountId?: string; available: Record<string, number>; error?: string } | null;
}

export interface PayoutSummary {
  totals: {
    requests: number; requestedAmount: number; paidCount: number; paidAmount: number; feesCollected: number;
    openCount: number; openAmount: number; returnedCount: number; returnedAmount: number;
  };
  byStatus: { status: string; count: number; amount: number; fee: number; net: number }[];
  byGateway: { gateway: string; automatic: boolean; count: number; net: number }[];
  byUserType: { userType: string; count: number; partners: number; requested: number; paid: number }[];
  daily: { date: string; requested: number; paid: number; count: number }[];
  queues: { kycPending: number; accountsPending: number; withdrawals: Record<string, { count: number; amount: number }> };
}

export interface PartnerReportRow {
  userId: string;
  name: string;
  phone?: string;
  email?: string;
  userType: string;
  kycStatus: KycStatus;
  earnings: number;
  incentives: number;
  topups: number;
  adjustments: number;
  otherCredits: number;
  otherDebits: number;
  withdrawn: number;
  reversed: number;
  txCount: number;
  lastTxAt?: string;
  requests: number;
  requestedAmount: number;
  paidCount: number;
  paidNet: number;
  fees: number;
  openAmount: number;
  returnedAmount: number;
  balance: number;
  currency?: string;
}

export interface LedgerRow {
  _id: string;
  user: PayoutPerson;
  type: string;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  currency: string;
  description?: string;
  referenceType?: string;
  referenceId?: string;
  createdAt: string;
}

export interface PartnerStatement {
  partner: { userId: string; name: string; phone?: string; email?: string; userType: string };
  period: { from?: string; to?: string };
  currency?: string;
  currentBalance: number;
  openingBalance: number;
  totalCredits: number;
  totalDebits: number;
  closingBalance: number;
  breakdown: { type: string; credit: number; debit: number; count: number }[];
  transactions: Omit<LedgerRow, 'user'>[];
  truncated: boolean;
  withdrawals: Withdrawal[];
  kyc: KycRow | { status: 'not_submitted' };
  accounts: PayoutAccountRow[];
}

type Params = Record<string, string | number | boolean | undefined>;
type Meta = { total: number; page: number; limit: number; totalPages: number; totals?: Record<string, number> };

async function list<T>(url: string, params: Params) {
  const res = await api.get<ApiResponse<T[]> & { meta: Meta }>(url, { params });
  return { rows: res.data.data || [], meta: res.data.meta };
}

export const payoutApi = {
  settings: async () => (await api.get<ApiResponse<PayoutSettings>>('/payouts/admin/settings')).data.data,
  saveSettings: async (body: Omit<Partial<PayoutSettings>, 'stripe'> & {
    stripe?: {
      webhookSecret?: string; platformWebhookSecret?: string; accountCountry?: string; statementDescriptor?: string;
      managedBankPayouts?: boolean; financialAccountId?: string;
    };
  }) =>
    (await api.put<ApiResponse<PayoutSettings>>('/payouts/admin/settings', body)).data.data,
  stripeStatus: async () => (await api.get<ApiResponse<StripeStatus>>('/payouts/admin/stripe-status')).data.data,
  runAutoPayoutNow: async () => (await api.post<ApiResponse<AutoPayoutRunSummary>>('/payouts/admin/auto-payout/run')).data.data,

  withdrawals: (params: Params) => list<Withdrawal>('/payouts/admin/withdrawals', params),
  withdrawal: async (id: string) => (await api.get<ApiResponse<Withdrawal & {
    account: PayoutAccountRow | null; kyc: KycRow | { status: string }; wallet?: { balance: number; currency: string; isFrozen?: boolean }; logs: PayoutLogEntry[];
  }>>(`/payouts/admin/withdrawals/${id}`)).data.data,
  /** Approve = pay: Stripe-payable accounts are paid through Stripe, others are recorded as paid. */
  approve: (id: string, body: { note?: string; transactionRef?: string }) => api.post(`/payouts/admin/withdrawals/${id}/approve`, body),
  reject: (id: string, reason: string) => api.post(`/payouts/admin/withdrawals/${id}/reject`, { reason }),
  payStripe: (id: string) => api.post(`/payouts/admin/withdrawals/${id}/pay-stripe`),
  markPaid: (id: string, body: { transactionRef: string; paymentMethod?: string; paidAt?: string; note?: string }) =>
    api.post(`/payouts/admin/withdrawals/${id}/mark-paid`, body),
  markFailed: (id: string, reason: string) => api.post(`/payouts/admin/withdrawals/${id}/mark-failed`, { reason }),
  sync: (id: string) => api.post(`/payouts/admin/withdrawals/${id}/sync`),

  kycList: (params: Params) => list<KycRow>('/payouts/admin/kyc', params),
  kyc: async (id: string) => (await api.get<ApiResponse<KycRow>>(`/payouts/admin/kyc/${id}`)).data.data,
  kycDocument: async (id: string, index: number) =>
    (await api.get<Blob>(`/payouts/admin/kyc/${id}/documents/${index}`, { responseType: 'blob' })).data,
  reviewKyc: (id: string, decision: 'verified' | 'rejected', reason?: string) =>
    api.post(`/payouts/admin/kyc/${id}/review`, { decision, reason }),

  accounts: (params: Params) => list<PayoutAccountRow>('/payouts/admin/accounts', params),
  reviewAccount: (id: string, decision: 'verified' | 'rejected' | 'disabled', reason?: string, email?: string) =>
    api.post(`/payouts/admin/accounts/${id}/review`, { decision, reason, email }),
  revealAccount: async (id: string) =>
    (await api.post<ApiResponse<{ accountNumber: string; routingNumber?: string; swiftCode?: string }>>(`/payouts/admin/accounts/${id}/reveal`)).data.data,
  refreshAccount: (id: string) => api.post(`/payouts/admin/accounts/${id}/refresh`),
  autoVerifyKyc: async (id: string) => (await api.post<ApiResponse<AutoCheck>>(`/payouts/admin/kyc/${id}/auto-verify`, undefined, { timeout: 120_000 })).data.data,
  autoVerifyAccount: async (id: string) => (await api.post<ApiResponse<AutoCheck>>(`/payouts/admin/accounts/${id}/auto-verify`, undefined, { timeout: 60_000 })).data.data,

  summary: async (params: Params) => (await api.get<ApiResponse<PayoutSummary>>('/payouts/admin/reports/summary', { params })).data.data,
  partnerReport: (params: Params) => list<PartnerReportRow>('/payouts/admin/reports/partners', params),
  statement: async (userId: string, params: Params) =>
    (await api.get<ApiResponse<PartnerStatement>>(`/payouts/admin/reports/partners/${userId}`, { params })).data.data,
  ledger: (params: Params) => list<LedgerRow>('/payouts/admin/reports/ledger', params),
  logs: (params: Params) => list<PayoutLogEntry>('/payouts/admin/logs', params),
};

/** Downloads an endpoint's `format=csv` export with the admin's auth headers. */
export async function downloadPayoutCsv(url: string, params: Params, filename: string) {
  const res = await api.get<Blob>(url, { params: { ...params, format: 'csv', page: undefined, limit: undefined }, responseType: 'blob' });
  const href = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = href;
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}
